/**
 * Coverage status display config (Specs/compliance-checklist-spec.md §2.3).
 * Status is derived server-side from links × section workflow state — these
 * are presentation-only mappings, following section-status.ts conventions.
 */

import type { CoverageStatus } from '@/lib/api'
import type { ResponseExpectation } from '@/types/extraction'

export interface CoverageStatusConfig {
  label: string
  variant: 'outline' | 'default' | 'warning' | 'review' | 'success'
}

export const coverageStatusConfig: Record<CoverageStatus, CoverageStatusConfig> = {
  unmapped: { label: 'Unmapped', variant: 'outline' },
  mapped: { label: 'Mapped', variant: 'default' },
  drafting: { label: 'Drafting', variant: 'warning' },
  in_review: { label: 'In review', variant: 'review' },
  addressed: { label: 'Addressed', variant: 'success' },
}

/**
 * The statuses `CoverageSummaryStrip` draws a button for, in strip order.
 *
 * This is the set a coverage filter may be set to, and it is narrower than
 * `CoverageStatus` on purpose: `mapped` has no button, so a filter holding it
 * could never be switched back off from the strip. Anything that can SET the
 * filter — the URL seed included — must validate against this list, or it can
 * seed a narrowing with no affordance to undo it.
 */
export const FILTERABLE_COVERAGE_STATUSES = [
  'unmapped',
  'drafting',
  'in_review',
  'addressed',
] as const satisfies readonly CoverageStatus[]

export type FilterableCoverageStatus = (typeof FILTERABLE_COVERAGE_STATUSES)[number]

export interface ExpectationConfig {
  label: string
  /**
   * Short stand-in for surfaces that show no pill. The table's "Where answered"
   * cell renders this ALONE for non-narrative rows, so it can never be null for
   * one — nulling it blanks the cell rather than tidying it.
   */
  hint: string | null
  /**
   * True when `hint` states something `label` does not, so it earns a place
   * BESIDE the pill as well as in place of it. "Evidence · attach only" is the
   * same word twice; "Post-award · not required for submission" is a fact the
   * label does not carry, and the drawer hides the Progress row for those, so
   * it is the only thing telling the reader this one is out of scope.
   */
  hintBeyondLabel: boolean
}

export const expectationConfig: Record<ResponseExpectation, ExpectationConfig> = {
  narrative: { label: 'Narrative', hint: null, hintBeyondLabel: false },
  evidence: { label: 'Evidence', hint: 'attach only', hintBeyondLabel: false },
  form: { label: 'Form', hint: 'form to complete', hintBeyondLabel: false },
  post_award: {
    label: 'Post-award',
    hint: 'not required for submission',
    hintBeyondLabel: true,
  },
}

export const EXPECTATION_ORDER: ResponseExpectation[] = ['narrative', 'evidence', 'form', 'post_award']

/** Artifact-name abbreviation for section chips, e.g. "Technical Response" → "TR". */
export function abbreviateArtifact(name: string): string {
  const words = name.split(/[\s-]+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase()
  return words
    .slice(0, 3)
    .map((w) => w[0]!.toUpperCase())
    .join('')
}
